import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useState } from 'react'

import type { FactField } from '@/services/api/api.types'

import { SortableField } from './SortableField'
import type { HeaderField } from './types'

interface SortableHeaderProps {
  header: HeaderField
  onFieldChange?: (field: FactField) => void
  onFieldReorder?: (headerId: string, activeId: string, overId: string) => void
}

export function SortableHeader({ header, onFieldChange, onFieldReorder }: SortableHeaderProps) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const { setNodeRef, setActivatorNodeRef } = useSortable({
    id: header.id,
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleFieldDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over || active.id === over.id) return

    const activeId = active.id as string
    const overId = over.id as string

    onFieldReorder?.(header.id, activeId, overId)
  }

  const activeField = activeId ? header.children?.find(child => child.id === activeId) : null

  return (
    <div ref={setNodeRef}>
      <SortableField
        field={header}
        id={header.id}
        onChange={onFieldChange}
        dragHandleRef={setActivatorNodeRef}
        headerData={header}>
        {header.children && header.children.length > 0 && (
          <DndContext
            sensors={sensors}
            onDragStart={handleFieldDragStart}
            onDragEnd={handleFieldDragEnd}
            collisionDetection={closestCenter}>
            <SortableContext items={header.children.map(child => child.id)} strategy={verticalListSortingStrategy}>
              {header.children.map(child => (
                <SortableField key={child.id} field={child} id={child.id} onChange={onFieldChange} />
              ))}
            </SortableContext>
            <DragOverlay>{activeField ? <SortableField field={activeField} id={activeField.id} /> : null}</DragOverlay>
          </DndContext>
        )}
      </SortableField>
    </div>
  )
}
